import type { PermissionCode } from './permissions';

/**
 * Integration event catalog (SPEC §68, Phase 3A, ADR-055). Events are written in the same
 * transaction as the change they describe and published on the event feed and to webhooks. A
 * client receives an event only while its roles grant `permission` (and, for `scoped` events,
 * cover the event's college or department), so an event never tells a client more than the API
 * would.
 */
export interface IntegrationEventDefinition {
  readonly type: string;
  readonly label: string;
  readonly description: string;
  readonly permission: PermissionCode;
  readonly scoped: boolean;
}

const e = (type: string, label: string, description: string, permission: PermissionCode, scoped = false) =>
  ({ type, label, description, permission, scoped }) as const;

export const INTEGRATION_EVENT_DEFINITIONS = [
  e(
    'payroll_period.locked',
    'Cut-off locked',
    'A payroll cut-off was locked; a new, final version of its payroll-ready report is available.',
    'payroll_report.read',
  ),
  e('payroll_period.processed', 'Cut-off processed', 'Accounting marked a locked cut-off processed and recorded the payroll reference.', 'payroll_report.read'),
  e(
    'payroll_period.reopened',
    'Cut-off reopened',
    'A locked or processed cut-off was reopened with authorization; its last report version is superseded until the next lock.',
    'payroll_report.read',
  ),
  e('sis_sync.finished', 'SIS sync finished', 'A submitted SIS sync was processed: committed, a dry run, rejected or failed, with its counts.', 'schedule.import'),
  e('hris_sync.finished', 'HRIS sync finished', 'A submitted HRIS sync was processed: committed, a dry run, rejected or failed, with its counts.', 'faculty.manage'),
  e('leave.approved', 'Leave approved', 'Leave was approved here or recorded from the HRIS; it excuses the sessions it covers.', 'leave.sync', true),
  e('leave.changed', 'Leave changed', 'The dates of approved leave changed: an early return, or a correction from the HRIS.', 'leave.sync', true),
  e('leave.cancelled', 'Leave cancelled', 'Approved leave was cancelled; the sessions it covered count again.', 'leave.sync', true),
] as const satisfies readonly IntegrationEventDefinition[];

export type IntegrationEventType = (typeof INTEGRATION_EVENT_DEFINITIONS)[number]['type'];
export const INTEGRATION_EVENT_TYPES = INTEGRATION_EVENT_DEFINITIONS.map((d) => d.type) as unknown as readonly [IntegrationEventType, ...IntegrationEventType[]];

const EVENT_BY_TYPE = new Map<string, IntegrationEventDefinition>(INTEGRATION_EVENT_DEFINITIONS.map((d) => [d.type, d]));

export function integrationEventDefinition(type: string): IntegrationEventDefinition | undefined {
  return EVENT_BY_TYPE.get(type);
}

/** The test event an administrator sends to one webhook; never on the feed. */
export const INTEGRATION_PING_EVENT = 'integration.ping';

/** OAuth 2.0 token endpoint, relative to the API base path (RFC 6749 §3.2). */
export const OAUTH_TOKEN_PATH = '/oauth/token';

/** Headers on every webhook request (ADR-056). */
export const WebhookHeaders = {
  EVENT: 'x-fas-event',
  EVENT_ID: 'x-fas-event-id',
  DELIVERY: 'x-fas-delivery',
  /** `t=<unix seconds>,v1=<hex HMAC-SHA256 of "<t>.<raw body>">` */
  SIGNATURE: 'x-fas-signature',
} as const;

/** Receivers should refuse signatures older than this (replay protection). */
export const WEBHOOK_SIGNATURE_TOLERANCE_SECONDS = 300;

/** Minutes after a failed attempt before the next one; after the last, the delivery gives up. */
export const WEBHOOK_RETRY_MINUTES = [1, 5, 30, 120, 360, 720, 1440] as const;

/** Attempts per delivery: the first plus one per retry interval. */
export const WEBHOOK_MAX_ATTEMPTS = WEBHOOK_RETRY_MINUTES.length + 1;

/** Active secrets per client: one in use plus one being rolled out. */
export const MAX_ACTIVE_CLIENT_SECRETS = 2;

/** Records one sync (SIS or HRIS) may carry; the sync routes accept bodies up to 8 MB. */
export const SYNC_MAX_ITEMS = 4000;

